/**
 * Deployment-level (Layer A) filtering helpers for the admin UI.
 *
 * The admin bundle is built per client. `platform.config.json` decides which games
 * and sports that client ships; the generator bakes those into
 * `@/generated/platform-client`. These helpers join that baked allow-list against the
 * shared `@low6-pickem/sports-config` registry so the UI only ever surfaces games and
 * sports the client actually deployed.
 *
 * Use `isGameEnabled` / `isGameTypeEnabled` / `isSportEnabled` for predicates, or the
 * pre-filtered `ENABLED_GAME_TYPES` / `ENABLED_SPORTS` lists for dropdowns and navigation.
 */
import {
  ENABLED_GAMES,
  ENABLED_GAME_CODES,
  ENABLED_SPORT_CODES,
  CONTENT_LOCALES,
  FEED_ENABLED,
  AUTH_PROVIDER,
} from '@/generated/platform-client';
import { GAME_TYPES, SUPPORTED_SPORTS } from '@low6-pickem/sports-config';
import type { GameKey, ContentLocale } from '@low6-pickem/platform-config';

const enabledGames = new Set<string>(ENABLED_GAMES);
const enabledGameCodes = new Set<string>(ENABLED_GAME_CODES);
const enabledSports = new Set<string>(ENABLED_SPORT_CODES);

/**
 * Whether a platform game key is deployed. Mirrors platform-config's own
 * `isGameEnabled(config, game)` against the artefact baked in at build time.
 *
 * Prefer this over `isGameTypeEnabled` when asking whether a *game module* ships — most
 * importantly for Lobby, which has no sports-config game-type code (it is the shell around
 * the games, not a playable contest), so a code-based predicate reports it absent even for
 * a client that does deploy it. That Lobby is a `GameKey` at all is a wrinkle in the
 * platform contract, not something this module chose — CAE-682 redesigns it.
 *
 * Takes the platform's `GameKey` union rather than a string: a mistyped key would otherwise
 * compile and silently report a deployed game as absent, suppressing working functionality.
 */
export function isGameEnabled(gameKey: GameKey): boolean {
  return enabledGames.has(gameKey);
}

/** Whether a sports-config game type code (e.g. 'pickem', 'squares', 'daily_reveal') is deployed. */
export function isGameTypeEnabled(code: string): boolean {
  return enabledGameCodes.has(code);
}

/** Whether a sports-config sport code (e.g. 'NFL', 'NBA', 'SOCCER') is deployed. */
export function isSportEnabled(code: string): boolean {
  return enabledSports.has(code);
}

/**
 * Whether this client's Game Managers author Contest and Question text in `locale` — the
 * gate on every secondary-locale field in the authoring forms (CAE-762).
 *
 * Reads the artefact per call rather than snapshotting it into a module-scope `Set` like
 * the predicates above. They snapshot to make repeated membership checks cheap over lists
 * that are re-tested on every render; this one answers from a one- or two-element list, so
 * the set buys nothing — and the live binding is what lets a form test vary the
 * declaration without re-importing the component under test.
 *
 * Takes the platform's `ContentLocale` union rather than a string: a typo would otherwise
 * compile and silently hide a field the client does author in, which reads to a Game
 * Manager as data loss rather than as a bug.
 */
export function isContentLocaleEnabled(locale: ContentLocale): boolean {
  return (CONTENT_LOCALES as readonly string[]).includes(locale);
}

/**
 * Whether this client has a sports feed.
 *
 * The gate on every admin surface that reads fixtures, teams, players or live stats — the
 * Sports section, Live Scoring, auto-grading, and feed-based question creation. The backend
 * does not register those routes on a feed-less instance either, so a surface left visible
 * here would 404 rather than merely come back empty.
 *
 * Distinct from `isSportEnabled`: a client can declare a sport (contests are stored against
 * one) while having no feed to fetch anything about it. That is exactly the manual-authoring
 * case, so the two questions must not be conflated.
 */
export function isFeedEnabled(): boolean {
  return FEED_ENABLED;
}

/**
 * Whether this instance authenticates Players with Clerk.
 *
 * Gates the Clerk credential fields on the tenant Security form. Instance-level, not per
 * tenant: this fork selects the auth style once, in platform config, so a tenant of a
 * non-Clerk instance has nowhere to use Clerk settings even if they were entered.
 */
export function isClerkAuth(): boolean {
  return AUTH_PROVIDER === 'clerk';
}

/** GAME_TYPES filtered to games this client deployed. */
export const ENABLED_GAME_TYPES = GAME_TYPES.filter(gt => isGameTypeEnabled(gt.code));

/** SUPPORTED_SPORTS filtered to sports this client deployed. */
export const ENABLED_SPORTS = SUPPORTED_SPORTS.filter(league => isSportEnabled(league.code));

/**
 * The enabled sports for a sport `<Select>`, plus the currently-selected sport when it isn't
 * in the enabled set (e.g. an existing record on a sport this client later disabled). Without
 * this the Select would have no matching option and render blank, risking the value being
 * dropped on save. The selected sport must still exist in the shared registry.
 */
export function sportsForSelect(selectedCode?: string | null): typeof SUPPORTED_SPORTS {
  if (!selectedCode || isSportEnabled(selectedCode)) return ENABLED_SPORTS;
  const selected = SUPPORTED_SPORTS.find(league => league.code === selectedCode);
  return selected ? [...ENABLED_SPORTS, selected] : ENABLED_SPORTS;
}
